import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '../../utils/cn';

type AuthAlertVariant = 'error' | 'success' | 'info';

const styles: Record<
  AuthAlertVariant,
  { wrap: string; icon: string; text: string; Icon: React.ElementType }
> = {
  error: {
    wrap: 'border-red-200 bg-red-50',
    icon: 'text-red-500',
    text: 'text-red-700',
    Icon: AlertCircle,
  },
  success: {
    wrap: 'border-emerald-200 bg-emerald-50',
    icon: 'text-emerald-600',
    text: 'text-emerald-800',
    Icon: CheckCircle2,
  },
  info: {
    wrap: 'border-blue-200 bg-blue-50',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    Icon: Info,
  },
};

interface AuthAlertProps {
  variant?: AuthAlertVariant;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const AuthAlert: React.FC<AuthAlertProps> = ({
  variant = 'error',
  title,
  children,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const { wrap, icon, text, Icon } = styles[variant];

  return (
    <motion.div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      className={cn('flex items-start gap-3 rounded-xl border px-4 py-3', wrap, className)}
      initial={reduceMotion ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', icon)} aria-hidden />
      <motion.div className="min-w-0">
        {title ? <p className={cn('text-sm font-semibold', text)}>{title}</p> : null}
        <p className={cn('text-sm leading-relaxed', text, title && 'mt-0.5')}>{children}</p>
      </motion.div>
    </motion.div>
  );
};
